// layout.row.v1 — horizontal flex with token-fixed gap.
// layout.column.v1 — same, vertical.
//
// Plugins use these instead of writing `flex items-center gap-N` by hand
// so the gap rhythm stays consistent. The gap scale is intentionally
// small (3 sizes) — if a plugin needs an unusual gap, that's a sign it
// should be a Section or a Card, not a free-form row.

import { type JSX, splitProps } from "solid-js"

export type Gap = "tight" | "default" | "loose"
export type Align = "start" | "center" | "end" | "baseline" | "stretch"
export type Justify = "start" | "center" | "end" | "between" | "around"

interface FlexProps {
  gap?: Gap
  align?: Align
  justify?: Justify
  wrap?: boolean
  children?: JSX.Element
  class?: string
  classList?: Record<string, boolean | undefined>
}

const gapClasses = {
  tight: "gap-1.5",
  default: "gap-3",
  loose: "gap-5",
} as const satisfies Record<Gap, string>

const alignClasses = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  baseline: "items-baseline",
  stretch: "items-stretch",
} as const satisfies Record<Align, string>

const justifyClasses = {
  start: "justify-start",
  center: "justify-center",
  end: "justify-end",
  between: "justify-between",
  around: "justify-around",
} as const satisfies Record<Justify, string>

export function Row(props: FlexProps) {
  const [local, rest] = splitProps(props, ["gap", "align", "justify", "wrap", "children", "class", "classList"])

  return (
    <div
      data-component="layout.row"
      classList={{
        "flex min-w-0": true,
        [gapClasses[local.gap ?? "default"]]: true,
        [alignClasses[local.align ?? "center"]]: true,
        [justifyClasses[local.justify ?? "start"]]: true,
        "flex-wrap": !!local.wrap,
        [local.class ?? ""]: !!local.class,
        ...(local.classList ?? {}),
      }}
      {...rest}
    >
      {local.children}
    </div>
  )
}

export function Column(props: FlexProps) {
  const [local, rest] = splitProps(props, ["gap", "align", "justify", "wrap", "children", "class", "classList"])

  return (
    <div
      data-component="layout.column"
      classList={{
        "flex flex-col min-w-0": true,
        [gapClasses[local.gap ?? "default"]]: true,
        [alignClasses[local.align ?? "stretch"]]: true,
        [justifyClasses[local.justify ?? "start"]]: true,
        [local.class ?? ""]: !!local.class,
        ...(local.classList ?? {}),
      }}
      {...rest}
    >
      {local.children}
    </div>
  )
}
